import React from 'react';
import { useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { OutreachShell } from '../components/OutreachShell';
import { useCampaign } from '../hooks/useCampaign';
import { useCampaignLive } from '../hooks/useCampaignLive';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { useReplyToast } from '../hooks/useReplyToast';
import { outreachBuilderPath } from '../outreachPaths';
import { outreachMessages } from '../outreachMessages';
import { SearchView } from './SearchView';
import { CampaignHeader } from './CampaignHeader';
import { CampaignTabs } from './CampaignTabs';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The campaign page `/outreach/campaigns/:campaignUuid` (design outreach-v2 g1, n1): the head and the
 * tabs Today | People | Results (`?tab=`). A draft opens the setup. While Plug still searches
 * (`searching`, `writing`, `review`) the search view of the momentum design shows under the head.
 */
const CampaignLiveScreenComponent: React.FC<{ accountId: number; campaignUuid: string }> = ({
  accountId,
  campaignUuid,
}) => {
  const intl = useIntl();
  const plan = useOutreachPlan();
  const { live, isLoading, error, retry } = useCampaignLive(accountId, campaignUuid);
  const { campaign } = useCampaign(accountId, campaignUuid);
  useReplyToast(accountId, campaignUuid);

  const content = () => {
    if (isLoading) {
      return <LoadingView />;
    }
    if (error !== null || live === undefined) {
      return <ErrorView error={error ?? undefined} retry={retry} />;
    }
    if (live.phase === 'draft') {
      return <Navigate to={outreachBuilderPath({ campaign: campaignUuid, song: live.song.catalogItemUuid })} replace />;
    }
    const isSearching = live.phase === 'searching' || live.phase === 'writing' || live.phase === 'review';
    return (
      <div className={styles.page}>
        <CampaignHeader accountId={accountId} live={live} campaign={campaign} />
        {isSearching ? (
          <SearchView accountId={accountId} live={live} canPitch={plan.canPitch} />
        ) : (
          <CampaignTabs accountId={accountId} live={live} campaign={campaign} />
        )}
      </div>
    );
  };

  const song = live?.song.title ?? '';
  return (
    <OutreachShell section="under" title={song} crumbs={[intl.formatMessage(outreachMessages.crumbPitch), song]}>
      {content()}
    </OutreachShell>
  );
};

export const CampaignLiveScreen = TypedMemo(CampaignLiveScreenComponent);
